'use client';

import * as React from 'react';
import { ArrowUpRight, CreditCard, DollarSign, TrendingUp } from 'lucide-react';

import { Payment } from '@tagadapay/crm-entities';
import { PaymentsTable } from './components/PaymentsTable';
import { ExportPaymentsDialog } from './components/export-payments-dialog';
import { usePaymentsList, useRefundPayment } from '@/lib/api/payments';
import { BulkRefundToastBody } from './components/bulk-refund-toast-body';
import { summarizeBulkRefund } from './utils/bulk-refund-summary';
import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { formatMoney } from "@/lib/money";
import { DateDisplay } from '@/components/date-display';
import { ResourceStatsCardGrid } from '@/components/ui/resource-stats-card';
import { useTablePage } from '@/hooks/use-table-page';
import {
  paymentsFilterMapping,
  paymentsQueryParamsMapping,
  paymentsSortMapping,
} from './utils/table-mappings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

/**
 * Payments page component - displays list of payments with comprehensive filtering
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(PaymentsPage, PERMISSIONS.PAYMENTS_VIEW);

function PaymentsPage() {
  const { t } = useTranslation('payments');
  const { toast } = useToast();

  // Use generic table page hook with filter and sort mappings
  const { searchQuery, setSearchQuery, tableState, setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: paymentsFilterMapping,
    sortMapping: paymentsSortMapping,
    queryParamsMapping: paymentsQueryParamsMapping,
  });

  // Dialog states
  const [selectedPayment, setSelectedPayment] = React.useState<Payment.Payment | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = React.useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = React.useState(false);
  const [refundRequestId, setRefundRequestId] = React.useState<string | null>(null);

  // Close dialogs when component unmounts
  React.useEffect(() => {
    return () => {
      setIsViewDialogOpen(false);
      setIsRefundDialogOpen(false);
      setSelectedPayment(null);
    };
  }, []);

  // Use tRPC to fetch payments
  const { data, isLoading, isFetching, isError, error, refetch } = usePaymentsList(queryInput);

  // Extract data
  const payments = (data?.payments || []) as Payment.Payment[];
  const total = data?.total || 0;
  const pageCount = data?.pageCount || 0;

  // Extract options - ensure object reference changes when data changes
  const options = React.useMemo(() => {
    const backendOptions = (data as any)?.options;
    if (!backendOptions) {
      return {
        status: [],
        currency: [],
        initiatedBy: [],
        reasonType: [],
        requireAction: [],
        processorName: [],
        paymentMethod: [],
      };
    }
    // Return the backend options directly to ensure reference changes
    return {
      status: backendOptions.status || [],
      currency: backendOptions.currency || [],
      initiatedBy: backendOptions.initiatedBy || [],
      reasonType: backendOptions.reasonType || [],
      requireAction: backendOptions.requireAction || [],
      processorName: backendOptions.processorName || [],
      paymentMethod: backendOptions.paymentMethod || [],
    };
  }, [data]);

  // Computed stats for analytics cards
  const successfulPayments = payments.filter((p: any) => p.status === 'succeeded').length;
  const totalAmount = payments.reduce((acc: number, p: any) => acc + (p.amount || 0), 0);
  const averagePayment = payments.length === 0 ? 0 : Math.round(totalAmount / payments.length);

  // Memoized stats cards configuration
  const statsCards = React.useMemo(
    () => [
      {
        title: t('totalPayments'),
        value: total,
        description: t('allTime'),
        icon: CreditCard,
      },
      {
        title: t('successful'),
        value: successfulPayments,
        description: t('completedPayments'),
        icon: TrendingUp,
      },
      {
        title: t('totalVolume'),
        value: formatMoney(totalAmount, 'USD'),
        description: t('paymentVolume'),
        icon: DollarSign,
      },
      {
        title: t('averagePayment'),
        value: payments.length === 0 ? '-' : formatMoney(averagePayment, 'USD'),
        description: t('perPayment'),
        icon: ArrowUpRight,
      },
    ],
    [total, successfulPayments, totalAmount, averagePayment, payments.length, t],
  );

  // Refund mutation
  const refundMutation = useRefundPayment();

  // Handle refund action
  const handleRefundConfirm = () => {
    if (!selectedPayment || refundRequestId === null) return;

    refundMutation.mutate(
      { paymentIds: [selectedPayment.id], requestId: refundRequestId },
      {
        onSuccess: () => {
          toast({
            title: t('refundSuccess'),
            description: t('refundSuccessDescription'),
          });
          setIsRefundDialogOpen(false);
          setSelectedPayment(null);
          refetch();
        },
        onError: (error) => {
          toast({
            title: t('refundFailed'),
            description: error.message || t('refundFailedDescription'),
            variant: 'destructive',
          });
        },
      },
    );
  };

  // Memoized event handlers
  const handleViewDetails = React.useCallback((payment: Payment.Payment) => {
    setSelectedPayment(payment);
    setIsViewDialogOpen(true);
  }, []);

  const handleRefund = React.useCallback((payment: Payment.Payment) => {
    setSelectedPayment(payment);
    setRefundRequestId(crypto.randomUUID());
    setIsRefundDialogOpen(true);
  }, []);

  const handleBulkRefund = React.useCallback(
    async (paymentIds: string[], requestId: string): Promise<string[]> => {
      try {
        const result = await refundMutation.mutateAsync({ paymentIds, requestId });
        refetch();
        // One payment selected: the single-refund path, which throws on failure.
        if (!result) {
          toast({
            title: t('refundSuccess'),
            description: t('bulkRefundSuccessDescription', { count: paymentIds.length }),
          });
          return [];
        }

        // One result per payment: say what went through, what did not and why. #TAG-3003
        const summary = summarizeBulkRefund(result);
        toast({
          title:
            summary.tone === 'success'
              ? t('refundSuccess')
              : summary.tone === 'partial'
                ? t('bulkRefund.partialTitle', { refunded: summary.refunded, total: summary.total })
                : t('refundFailed'),
          description: <BulkRefundToastBody summary={summary} />,
          variant: summary.tone === 'success' ? 'default' : 'destructive',
        });
        return summary.failedIds;
      } catch (error) {
        toast({
          title: t('refundFailed'),
          description: error instanceof Error ? error.message : t('refundFailedDescription'),
          variant: 'destructive',
        });
        return paymentIds;
      }
    },
    [refundMutation, refetch, toast, t],
  );

  const [exportOpen, setExportOpen] = React.useState(false);
  const handleExportPayments = React.useCallback(() => setExportOpen(true), []);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Page header */}
      <PageHeader title={t('title')} description={t('description')} />

      {/* Stats cards */}
      {/* <ResourceStatsCardGrid cards={statsCards} loading={isLoading} /> */}

      {/* Payments table */}
      <TableCard>
        <PaymentsTable
          payments={payments as any}
          total={total}
          pageCount={pageCount}
          options={options}
          isLoading={isLoading || isFetching}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTableStateChange={setTableState}
          onRefetch={refetch}
          onViewDetails={handleViewDetails}
          onRefund={handleRefund}
          onBulkRefund={handleBulkRefund}
          onExportPayments={handleExportPayments}
          isRefunding={refundMutation.isPending}
        />

        {/* Error message */}
        {isError && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('loadError')}
          </div>
        )}
      </TableCard>

      <ExportPaymentsDialog open={exportOpen} onOpenChange={setExportOpen} />

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="mx-4 sm:max-w-[425px] md:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('paymentDetails')}</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="grid gap-3 py-3 sm:gap-4 sm:py-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('id')}:</span>
                <span className="break-all font-mono text-xs sm:col-span-3">{selectedPayment.id}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('date')}:</span>
                <span className="text-sm sm:col-span-3">
                  <DateDisplay value={selectedPayment.createdAt} />
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('customer')}:</span>
                <span className="break-all text-sm sm:col-span-3">
                  {selectedPayment.customer?.email || t('notAvailable')}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('amount')}:</span>
                <span className="font-medium sm:col-span-3">
                  {formatMoney(selectedPayment.amount, selectedPayment.currency || 'USD')}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('status')}:</span>
                <span className="capitalize sm:col-span-3">{selectedPayment.status}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
                <span className="text-sm font-medium">{t('card')}:</span>
                <span className="sm:col-span-3">
                  {selectedPayment.paymentInstrument?.card ? (
                    <>
                      {selectedPayment.paymentInstrument.card.brand} ••••{' '}
                      {selectedPayment.paymentInstrument.card.last4}
                    </>
                  ) : (
                    t('notAvailable')
                  )}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <AlertDialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('refundPayment')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>{t('confirmRefundQuestion')}</p>
                {selectedPayment && (
                  <div className="space-y-1">
                    <p className="font-medium">
                      {t('amount')}: {formatMoney(selectedPayment.amount, selectedPayment.currency || 'USD')}
                    </p>
                    <p className="text-sm">{t('customer')}: {selectedPayment.customer?.email || t('notAvailable')}</p>
                  </div>
                )}
                <p className="text-destructive font-medium">{t('actionCannotBeUndone')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={refundMutation.isPending}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRefundConfirm}
              disabled={refundMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {refundMutation.isPending ? t('processing') : t('refundPayment')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
